<template>
  <div class="app" id="app" :class="{ 'auth-view': isAuth, 'no-min-width': isRouteMobileResponsive }">
    <icons-sprite />
    <transition name="fade" mode="out-in">
      <switching-teams v-if="switchingTeam && switchTeam" :team="switchTeam" />
    </transition>
    <template v-if="!(switchingTeam && switchTeam)">
      <badge-sprite />
      <language-icons-sprite />
      <temporary-mobile v-if="false" />
      <free-top-bar v-if="showFreeTopBar" />
      <trial-top-bar v-if="showTrialTopBar" />
      <global-sidenav v-if="!isAuth && $store.state.hasLoadedFirstRoute"
        :has-unread-notifications="hasUnreadNotifications" :notifications="notifications"
        :loading-notifications="loadingNotifications"
        :with-topbar = "showTrialTopBar || showFreeTopBar"/>
      <wisdom-settings-slide-in />
      <import-dialog />
      <main class="main" :class="{ 'with-topbar': showTrialTopBar || showFreeTopBar }">
        <auth-layout v-if="isAuth" :completed="authCompleted">
          <transition name="only-down" :duration="200" mode="out-in">
            <router-view @completed="authCompleted = true"></router-view>
          </transition>
        </auth-layout>
        <template v-else>
          <transition name="fade" :duration="200" mode="out-in">
            <router-view></router-view>
          </transition>
        </template>
        <transition name="fade">
          <upload-list v-if="!isAuth" :list="$store.state.addMedia.files"
          :abort="() => $store.dispatch('addMedia/abort')" />
        </transition>

        <achievements-slide-in />
        <apps-sidepanel />
        <person-dialog v-if="!!computedUser" />
        <add-member-modal />
        <playlist-create />
      </main>
      <transcript-editor />
      <trim-editor />
      <!--    <feedback-button />-->
      <widget-editor />
      <wisdom-editor />
      <manage-payment :url="paymentUrl" />
      <sitemap-selector />
      <spotlight-search />
      <resource-takeover-confirmation-dialog />
      <hub-create />
      <switch-hub-dialog ref="switchHubDialog" :target-hub="switchHub" @accept="handleSwitchHub" @close="switchHub = {}" />
      <template v-if="showOverlay">
        <div class="modal show"></div>
        <div class="modal-backdrop"></div>
      </template>
      <subscription-modal />
      <playlist-schedule-modal />
      <!-- Temporary Password Dialog -->
      <temporary-password-dialog v-if="temporaryPasswordData" :data="temporaryPasswordData" />
    </template>
  </div>
</template>
<script>
import File from '@app2/models/File';
import Folder from '@app2/models/Folder';
import Widget from '@app2/models/Widget';
import Playlist from '@app2/models/Playlist';
import eventBus from '@app2/core/eventBus';
import Echo from '@app2/api/Echo';
import getAllModels from '@app2/utils/allModels';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapSearchState } = createNamespacedHelpers('search');
import rootVue from "@app2/app"
import exportRouter from '@app2/modules/Audience/Export/export.router'
import importRouter from '@app2/modules/Audience/Import/import.router'
import playlistRouter from '@app2/modules/Library/Builder/playlist.router'
import playlistApp from '@app2/modules/Library/Builder/playlist.app'
import { debounce, throttle } from 'lodash'
import hubShortcutMixin from "@app2/utils/hub-shortcut-mixin.js"
import axios from '@app2/api/axios';

export default {
  name: 'app',
  metaInfo() {
    return {
      title: 'Membership.io',
      titleTemplate: `Membership.io | %s`
    }
  },
  watch: {
    '$route.fullPath'() {
      eventBus.$emit('routeUpdated')
    },
    $route(to, from) {
      this.updateMetaTag();
    },
  },
  components: {
    AchievementsSlideIn: () => import("@app2/modules/Achievements/SlideIn/App.vue"),
    FeedbackButton: () => import("@app2/core/Components/Feedback"),
    IconsSprite: () => import('@app2/core/Icons'),
    BadgeSprite: () => import('@utils/Badge/Sprite'),
    LanguageIconsSprite: () => import('@app2/core/Languages'),
    TrialTopBar: () => import('@app2/core/Components/Banner/TrialTopBar'),
    FreeTopBar: () => import('@app2/core/Components/Banner/FreeTopBar'),
    UploadList: () => import('@app2/core/Components/UploadList/Index.vue'),
    GlobalSidenav: () => import('@app2/core/Components/Sidenav/Container.vue'),
    TranscriptEditor: () => import('@app2/modules/Library/Media/TranscriptEditor/Pages/Index'),
    TrimEditor: () => import('@app2/modules/Library/Media/TrimEditor/Pages/Index'),
    TemporaryMobile: () => import('@app2/core/TemporaryMobile'),
    ImportDialog: () => import('@app2/modules/Audience/Import/App'),
    WidgetEditor: () => import('@app2/modules/Widgets/Pages/Index'),
    WisdomEditor: () => import('@app2/modules/Wisdom/Pages/Index'),
    SitemapSelector: () => import('@app2/modules/Sitemaps/Index.vue'),
    ManagePayment: () => import('@app2/modules/AccountSettings/Components/Modals/ManagePayment.vue'),
    AuthLayout: () => import('@app2/modules/Auth/Components/AuthLayout'),
    WisdomSettingsSlideIn: () => import('@app2/modules/Apps/Pages/Sidepanel/Wisdom/Builder/App.vue'),
    AppsSidepanel: () => import('@app2/modules/Apps/Pages/Sidepanel/Index'),
    SpotlightSearch: () => import('@app2/modules/Spotlight/Index.vue'),
    PersonDialog: () => import('@app2/modules/Audience/Person/App'),
    SwitchingTeams: () => import('@app2/core/Components/SwitchingTeams'),
    ResourceTakeoverConfirmationDialog: () => import('@app2/core/Components/ResourceTakeoverConfirmationDialog.vue'),
    PlaylistCreate: () => import('@app2/modules/Library/Builder/Components/PlaylistCreate.vue'),
    HubCreate: () => import('@app2/modules/Hubs/Create/Index.vue'),
    SwitchHubDialog: () => import('@app2/modules/Hubs/Preview/Components/SwitchHubDialog.vue'),
    AddMemberModal: () => import('@app2/modules/Audience/Components/Modals/AddMemberModal.vue'),
    SubscriptionModal: () => import('@app2/modules/AccountSettings/Components/Subscription/SubscriptionModal.vue'),
    PlaylistScheduleModal: () => import("@app2/modules/Library/Builder/Pages/Playlist/Schedule"),
    TemporaryPasswordDialog: () => import('@app2/modules/Audience/TemporaryPassword/App'),
  },
  mixins: [require('@app-spark/spark'), hubShortcutMixin],
  computed: {
    isAuth() {
      return (this.$route.meta && this.$route.meta.auth) || this.$route.name === 'onboarding';
    },
    widgets() {
      return Widget.all();
    },
    ...mapSearchState(['filters']),
    trial() {
      return this.$store.getters['auth/GET_TRIAL'];
    },
    isFreePlan() {
      return this.$store.getters['auth/GET_IS_FREE_PLAN'];
    },
    computedUser() {
      return this.$store.getters['auth/GET_USER'];
    },
    activeSubscription() {
      return this.$store.getters['auth/GET_SUBSCRIPTION'];
    },
    subscriptionIsCancelled() {
      return this.activeSubscription?.status === 'cancelled';
    },
    activePaymentSource() {
      return this.$store.getters['auth/GET_PAYMENT_SOURCE'];
    },
    trialEnded() {
      return this.$store.getters['auth/GET_TRIAL_ENDED'];
    },
    isRouteMobileResponsive() {
      const isHubPreview = this.$route.name === 'hubPreviewDashboard';
      
      if (isHubPreview) {
        // Hub preview is responsive on XS screens by default, unless desktop is forced
        const isMobileXs = this.$store.getters['isMobileXs'];
        const forceDesktopView = sessionStorage.getItem('hubPreview_forceDesktopView') === 'true';
        return isMobileXs && !forceDesktopView;
      }
      
      return this.mobileResponsiveRoutes.includes(this.$route.name);
    },
    isMobileHubPreview() {
      if (this.$route.name !== 'hubPreviewDashboard') return false;
      
      // Show mobile UI elements based on screen size and sessionStorage
      const isMobileXs = this.$store.getters['isMobileXs'];
      const isTablet = this.$store.getters['isTablet'];
      const forceDesktopView = sessionStorage.getItem('hubPreview_forceDesktopView') === 'true';
      
      // True when on tablet/mobile, but respect force desktop on XS
      return isTablet && !(isMobileXs && forceDesktopView);
    },
    showTrialTopBar() {
      return this.$route.name !== 'onboarding' && !this.isAuth && (!!this.trial || this.trialEnded && this.subscriptionIsCancelled && !this.activePaymentSource)
    },
    showFreeTopBar() {
      return this.$route.name !== 'onboarding' && !this.isAuth && this.isFreePlan
    }
  },
  data() {
    return {
      achievementApp: null,
      paymentUrl: null,
      authCompleted: false,
      showOverlay: false,
      switchingTeam: false,
      switchTeam: null,
      switchHub: null,
      // Mobile responsive routes
      mobileResponsiveRoutes: ['watch', 'hubPreviewDashboard'],
      temporaryPasswordData: null,
      retrying: false
    }
  },
  async mounted() {
    if (this.$store.getters['auth/GET_PLAN_ID']?.includes('trial') && this.$store.getters['auth/GET_USER']?.meta?.trial_hash) {
      this.$store.dispatch('auth/fetchPlan', this.$store.getters['auth/GET_USER'].meta.trial_hash);
    }
    await this.$store.dispatch('auth/fetchUser');
    this.updateMetaTag();
    
    // Add resize listener for viewport meta tag updates
    this.resizeHandler = throttle(() => {
      this.updateMetaTag();
    }, 300);
    window.addEventListener('resize', this.resizeHandler);
    
    if (this.$store.state.auth.user?.id) this.listenPusher();
    eventBus.$on('show-temporary-password-dialog', (data) => {
      this.showTemporaryPasswordDialog(data);
    });
    eventBus.$on('loggedIn', () => {
      this.listenPusher();
      this.loadDataForAuthenticatedUser();
    });
    eventBus.$on('toggleOverlay', (val) => {
      this.showOverlay = val;
    });
    eventBus.$on('switchTeam', (team) => {
      this.switchTeam = team;
      this.switchingTeam = true;
    });
    eventBus.$on('switch-hub', this.handleShowSwitchHubDialog);
    eventBus.$on('loggedOut', () => {
      getAllModels().forEach((model) => {
        if (!model || !model.deleteAll) return;
        try {
          model.deleteAll();
        } catch (e) { }
      });
    });
    eventBus.$on('auth/unauthenticated', () => {
      this.$store.dispatch('auth/unauthenticated');
    });
    eventBus.$on('manage-payment-url', (url) => {
      this.paymentUrl = url;
    });
    eventBus.$on('open-in-library', folder => {
      this.$bvModal.hide('apps-dialog');
    });
    window.addEventListener('message', async (event) => {
      if (event.origin.includes('chargebee.com') && (event.data?.key === "cb.payment_source.add" && event.data?.status === "success") || event.data?.key === "cb.success") {
        this.$store.commit('auth/SET_HAS_PAYMENT_METHOD', true);
        this.$bvModal.hide('manage-payments');
        this.$bvModal.hide('checkout-modal');
        eventBus.$emit('paymentMethodAdded');
        if (this.$route.name !== 'OnboardingDetails') await this.$store.dispatch('auth/fetchUser', true);
      }
    });
    if (localStorage?.redirect_hub && this.$route.name !== 'login') {
      this.redirectHubRequest();
    }
  },
  methods: {
    showTemporaryPasswordDialog(data) {
      this.temporaryPasswordData = data;
      setTimeout(() => {
        this.$bvModal.show('temporary-password-dialog');
      }, 300);
    },
    handleShowSwitchHubDialog(hub) {
      this.switchHub = hub;
      if (this.$refs.switchHubDialog && this.$route.params.hash !== hub.hash) {
        this.$refs.switchHubDialog.show();
      }
    },
    handleSwitchHub(hash) {
      this.$store.commit('v2/SET_ACTIVE_HUB_HASH', hash);
      this.$router.push({ name: 'hubPreviewDashboard', params: { hash } });
      this.switchHub = null;
      this.$bvModal.hide('switch-hub-dialog');
      this.$root.$emit('bv::hide::popover');
    },
    debouncer: debounce((cb, args) => cb(args), 500),
    toggleMediaModal() {
      if (this.$can('hours')) {
        this.$root.$emit('bv::toggle::modal', 'add-media', '#media-toggle');
      } else {
        if (!!this.trial) {
          this.$bvModal.show('trial-limit-modal');
        } else {
          this.$bvModal.show('quota-limit-modal');
        }
      }
      // this.$toasted.show('Cannot add anymore files, quota limit reached!', { type: 'error' });
    },
    updateMetaTag() {
      if (!this.$store.getters['isTablet'] || !this.$route.name) return; // Skip if not tablet view or no route name

      const isHubPreview = this.$route.name === 'hubPreviewDashboard';
      const isMobileXs = this.$store.getters['isMobileXs']; // <= 576px
      const forceDesktopView = sessionStorage.getItem('hubPreview_forceDesktopView') === 'true';

      let shouldBeResponsive;
      if (isHubPreview) {
        if (isMobileXs) {
          // Mobile XS (<=576px): responsive by default, desktop only when forced
          shouldBeResponsive = !forceDesktopView;
        } else {
          // Tablet (577-1200px): always desktop view
          shouldBeResponsive = false;
        }
      } else {
        // Other routes follow normal responsive rules
        shouldBeResponsive = this.isAuth || this.mobileResponsiveRoutes.includes(this.$route.name);
      }
      
      // Update viewport meta tag
      const viewportEl = document.querySelector('head meta[name="viewport"]');
      viewportEl.setAttribute('content', shouldBeResponsive 
        ? 'width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0'
        : 'width=1200');

      if (!shouldBeResponsive) {
        this.$nextTick(() => {
          this.$store.commit('setIsTablet');
          this.$store.commit('setIsMobile');
          this.$store.commit('setIsMobileXs');
        });
      }
    },
    async fileSuggestionToast(event) {
      await File.api().get(event.file.hash, { params: { include: ['folder', 'tags'] } })
      this.debouncer(() => {
        this.$toasted.show(`<p class="txt-body mb-1">Searchie AI suggestions for <b>${event.file.title}</b> are ready to view.</p>`, {
          duration: 120000,
          type: 'success',
          position: 'bottom-right',
          className: 'searchie-toast searchie-toast-small',
          action: [
            {
              text: 'View suggestions',
              onClick: (e, toastObject) => {
                if (this.$route.path.includes('/library')) {
                  playlistRouter.replace({ name: 'MediaPage', params: { hash: event.file.hash, noBackRoute: true } }).then(() => {
                  })
                    .catch(() => { })
                    .finally(() => {
                      this.$nextTick(() => {
                        playlistApp.$bvModal.show('playlist-form-dialog');
                        setTimeout(() => {
                          this.$bvModal.show('ai-suggestions-modal');
                          eventBus.$emit('open-searchieai-modal')
                        }, 300)
                      });
                    });
                  toastObject.goAway(0)
                }
                if (!this.$route.path.includes('/library')) {
                  setTimeout(() => {
                    const route = this.$router.resolve({ name: 'library', query: { file: event.file.hash, showAi: true } });
                    window.open(route.href, '_blank')
                  }, 300)
                  toastObject.goAway(0)
                }
              }
            },
            {
              text: 'Dismiss',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
              },
            },
            {
              text: 'X',
              class: 'close',
              onClick(e, toastObject) {
                toastObject.goAway(0);
              }
            }
          ]
        });
      }, 25000)
    },
    listenPusher() {
      Echo.private('user.' + Spark.userId)
        .listen('TranscriptionVersionUpdated', (event) => {
          if (event.fileId && event.result) {
            eventBus.$emit('TranscriptionVersionUpdated', event)
          }
        })
        .listen('PaymentEnabled', (event) => {
          console.log('PaymentEnabled: ', event)
          eventBus.$emit('PaymentEnabled', event)
        })
        .listen('FileSuggestionCreated', (event) => {
          console.log('FileSuggestionCreated: ', event)
          eventBus.$emit('file-suggestion-created', { type: event.type, fileHash: event.file.hash })
          if (this.$cant('searchie-ai') || this.$can('copilot')) return;
          localStorage.setItem('newSuggestionKey', event.type)
          this.fileSuggestionToast(event)
        })
        .listen('FileIsTooBig', (event) => {
          console.log('FileIsTooBig', event)
          this.$toasted.show(`Warning: The "${event.file.title}" file size exceeds 30GB`, { type: 'info' });
        })
        .listen('FileCopilotProcessed', async (event) => {
          console.log('FileCopilotProcessed', event)
          eventBus.$emit('FileCopilotProcessed', event.file)
          File.insertOrUpdate({ data: event.file });
          // await File.api().get(event.file_hash, { params: {include: ['folder', 'tags']} })
        })
        .listen('FileCopilotFailed', async (event) => {
          console.log('FileCopilotFailed', event)
        })
        .listen('ReplyCommentCreated', (event) => {
          console.log("ReplyCommentCreated", event)
        })
        .listen('FolderUpdated', (event) => {
          console.log("FolderUpdated", event)
          Folder.insertOrUpdate({ data: event.folder });
        })
        .listen('MediaTrimProcessing', (event) => {
          eventBus.$emit('MediaTrimProcessing', event.file)
        })
        .listen('FileStatusUpdated', async (event) => {
          console.log('FileStatusUpdated', event)
          if (event.file.hash) {
            const { data } = await File.api().get(event.file.hash, { params: {include: ['folder', 'tags']} })
            File.insertOrUpdate({ data: data });
          }
          eventBus.$emit('FileStatusUpdated', event.file)
          if (event.file.status_transcription === 'transcription_processed') {
            this.$store.commit('v2/library/SET_IMPORT_ALL_FILES', event.file.hash);
          }
          this.getNotifications({ file_hash: event.file.hash });
        })
        .listen('PlaylistProcessingStatusUpdated', (event) => {
          console.log("PlaylistProcessingStatusUpdated", event)
          const playlist = Playlist.find(event.playlist.hash)
          if (playlist) Playlist.insertOrUpdate({ data: Object.assign(playlist, event.playlist)});
          eventBus.$emit('PlaylistProcessingStatusUpdated', event.playlist)
          eventBus.$emit('playlist-updated', event.playlist.hash)
        })
        .listen('AudienceImportSuccess', (event) => {
          console.log("AudienceImportSuccess", event)
          this.$toasted.show(`<p class="txt-body mb-1">Import <b>${event.import.title}</b> is complete and ready to view!</p>`, {
            duration: 7000,
            type: 'success',
            action: {
              text: 'View imports',
              onClick: (e, toastObject) => {
                importRouter.push({ name: 'RecentImports' }).catch(() => { })
                rootVue.$bvModal.show('import-dialog');
              },
            },
          });
        })
        .listen('AudienceExportGenerated', (event) => {
          console.log("AudienceExportGenerated", event)
          eventBus.$emit('audienceExportGenerated', event.exportData)
          if (event.exportData.status === 'done') {
            this.$toasted.show(`<p class="txt-body mb-1">Export <b>${event.exportData.title}</b> is ready for download!</p>`, {
              duration: 7000,
              type: 'success',
              action: {
                text: 'View exports',
                onClick: (e, toastObject) => {
                  exportRouter.push({ name: 'RecentExports' }).catch(() => { })
                  rootVue.$bvModal.show('export-dialog');
                },
              },
            });
          }
          if (event.exportData.status === 'failed') {
            this.$toasted.show(`<p class="txt-body mb-1">Export <b>${event.exportData.title}</b> could not be generated.</p>`, {
              duration: 7000,
              type: 'error',
              action: {
                text: 'View exports',
                onClick: (e, toastObject) => {
                  exportRouter.push({ name: 'RecentExports' }).catch(() => { })
                  rootVue.$bvModal.show('export-dialog');
                },
              },
            });
          }
        })
        .listen('AudienceImportSuccess', (event) => {
          console.log("AudienceImportSuccess 2", event)
          this.getNotifications();
        })
        .listen('AudienceImportFailed', (event) => {
          console.log("AudienceImportFailed", event)
          this.getNotifications();
        })
        .listen('FileTagAttached', (event) => {
          if ( Array.isArray(event) && !event.length ) return
          console.log("FileTagAttached", event)
          eventBus.$emit('refresh-playlists')
        })
        .listen('FileTagDetached', (event) => {
          if ( Array.isArray(event) && !event.length ) return
          console.log("FileTagDetached", event)
          eventBus.$emit('refresh-playlists')
        })
        .listen('UserSessionUpdated', (event) => {
          this.$store.dispatch('auth/fetchUser'); //Check if user is still logged in
        })
        /**
         * ResourceTakeoverEvent listener
         * Triggered when another user takes over a resource the current user was editing
         * Displays a notification and propagates the event through the eventBus
         * @param {Object} event - Event data containing resource information
         * @param {Object} event.new_user - User who took over the resource
         * @param {string} event.resource_type - Type of resource that was taken over
         */
        .listen('ResourceTakeoverEvent', (event) => {
          // Create a notification for the user whose resource was taken over
          if (event.new_user.id === this.$store.getters['auth/GET_USER'].id) {
            // You're the one doing the takeover
            return;
          }
          eventBus.$emit('ResourceTakeoverEvent', event);
          this.$toasted.show(
            `<p class="txt-body mb-1">${event.new_user.name} has taken over the ${event.resource_type} you were editing.</p>`,
            { 
              type: 'warning',
              duration: 7000
            }
          );
        })
        /**
         * ResourceDeletedEvent listener
         * Triggered when another user deletes a resource the current user was viewing/editing
         * Displays a notification and propagates the event through the eventBus
         * Components can listen for this event to handle navigation or UI updates accordingly
         * 
         * @param {Object} event - Event data containing deletion information
         * @param {Object} event.deleted_by - User who deleted the resource
         * @param {string} event.resource_type - Type of resource that was deleted
         * @param {string|number} event.resource_id - ID of the resource that was deleted
         */
        .listen('ResourceDeletedEvent', (event) => {
          // Create a notification for the user whose resource was deleted
          if (event.deleted_by.id === this.$store.getters['auth/GET_USER'].id) {
            // You're the one doing the deletion
            return;
          }
          eventBus.$emit('ResourceDeletedEvent', event);
          this.$toasted.show(
            `<p class="txt-body mb-1">${event.deleted_by.name} has deleted the ${event.resource_type} you were editing.</p>`,
            { 
              type: 'warning',
              duration: 7000
            }
          );
        })
        /**
         * ResourceUpdatedEvent listener
         * Triggered when another user updates a resource the current user was viewing/editing
         * Displays a notification and propagates the event through the eventBus
         * Components can listen for this event to handle navigation or UI updates accordingly
         * 
         * @param {Object} event - Event data containing update information
         * @param {Object} event.updated_by - User who updated the resource
         * @param {string} event.resource_type - Type of resource that was updated
         * @param {string|number} event.resource_id - ID of the resource that was updated
         */
        .listen('ResourceUpdatedEvent', (event) => {
          // Create a notification for the user whose resource was updated
          if (event.updated_by.id === this.$store.getters['auth/GET_USER'].id) {
            // You're the one doing the update
            return;
          }
          eventBus.$emit('ResourceUpdatedEvent', event);
          this.$toasted.show(
            `<p class="txt-body mb-1">${event.updated_by.name} has updated the ${event.resource_type} you were editing.</p>`,
            { 
              type: 'warning',
              duration: 7000
            }
          );
        })
        .notification((notification) => {
          this.getNotifications();
          const toastOptions = { duration: 7000 };
          if (notification.action_text) {
            toastOptions.action = {
              text: notification.action_text,
              onClick: (e, toastObject) => {
                if (notification.action_url) this.$router.push(notification.action_url);
                else {
                  this.confirmAddMedia(notification.file)
                  toastObject.goAway()
                }
              },
            };
          }
          this.$toasted.show(`<p class="txt-body mb-1">${notification.body}</p>`, toastOptions);
        });
    },
    async confirmAddMedia(item) {
      if (this.retrying) return;
      if (this.$cant('hours')) {
        if (!!this.trial) {
          this.$bvModal.show('trial-limit-modal');
        } else {
          this.$bvModal.show('quota-limit-modal');
        }
        return;
      }
      try {
        this.retrying = true;
        await axios.post(`/api/files/${item.hash}/process`);
      } catch (e) {
        const message = e.response?.data?.message || 'Could not start processing file, something went wrong.';
        this.$toasted.show(message, { type: 'error' });
      } finally {
        this.retrying = false;
      }
    },
  },
  beforeDestroy() {
    eventBus.$off('switch-hub', this.handleShowSwitchHubDialog);
    eventBus.$off('show-temporary-password-dialog', this.showTemporaryPasswordDialog);
    
    // Clean up resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
};
</script>
